/**
 * EL SALDO DIA POR DIA (SALDOS-DIA-T2; pedido de Josse 1-oct, Ares #20879).
 *
 * Debajo de las tarjetas de #/saldos: por cada medio, una fila por dia desde la base con entradas, salidas, saldo del
 * sistema, lo que se conto (caja) o lo que dice el extracto (banco), la diferencia del dia y la acumulada. Los dias con
 * faltante van en rojo y el dia que mas bajo el saldo se dice arriba.
 *
 * Contrato (nexo-api 395, b9183d9): GET /api/v1/businesses/{id}/saldos/diario?desde&hasta&medio ->
 *   {desde, hasta, fechaBase, dias:[{fecha, medio, entradas, salidas, saldoSistema, arqueos, contado, esperado,
 *    conExtracto, bancoEntradas, bancoSalidas, nexoEntradas, noCruzo:{creditos, montoCreditos, debitos, montoDebitos},
 *    diferenciaDia (null = sin medida), diferenciaAcumulada}]}
 *
 * Puro: sin DOM. Lo mide scripts/check-saldos-dia.mjs.
 *
 * @module domain/saldosDia
 */

const num = (v) => (v === null || v === undefined || v === '' ? null : (Number.isFinite(Number(v)) ? Number(v) : null));

/** Un dia del contrato -> la fila que se pinta. diferenciaDia null queda null (sin medida NO es cero). */
export function leerDia(d) {
  const x = d && typeof d === 'object' ? d : {};
  const nc = x.noCruzo && typeof x.noCruzo === 'object' ? x.noCruzo : null;
  return {
    fecha: String(x.fecha || '').slice(0, 10),
    medio: String(x.medio || '').toLowerCase(),
    entradas: num(x.entradas) ?? 0,
    salidas: num(x.salidas) ?? 0,
    saldoSistema: num(x.saldoSistema) ?? 0,
    arqueos: num(x.arqueos),
    contado: num(x.contado),
    esperado: num(x.esperado),
    conExtracto: x.conExtracto === true ? true : x.conExtracto === false ? false : null,
    bancoNeto: num(x.bancoEntradas) === null && num(x.bancoSalidas) === null ? null : (num(x.bancoEntradas) ?? 0) - (num(x.bancoSalidas) ?? 0),
    nexoEntradas: num(x.nexoEntradas),
    noCruzo: nc ? { creditos: num(nc.creditos) ?? 0, montoCreditos: num(nc.montoCreditos) ?? 0, debitos: num(nc.debitos) ?? 0, montoDebitos: num(nc.montoDebitos) ?? 0 } : null,
    diferenciaDia: num(x.diferenciaDia),
    diferenciaAcumulada: num(x.diferenciaAcumulada) ?? 0
  };
}

/** La respuesta del cliente -> {ok, fechaBase, porMedio:{medio: [dias desc]}} o {ok:false, code}. Sin lista = error. */
export function diasDesdeRespuesta(res) {
  if (!res || !res.ok) return { ok: false, code: res?.code ?? res?.error?.code ?? 'NETWORK' };
  const d = res.data?.data ?? res.data ?? {};
  if (!Array.isArray(d.dias)) return { ok: false, code: 'FORMA' };
  const porMedio = {};
  for (const fila of d.dias.map(leerDia).filter((f) => f.fecha && f.medio)) (porMedio[fila.medio] ||= []).push(fila);
  for (const m of Object.keys(porMedio)) porMedio[m].sort((a, b) => b.fecha.localeCompare(a.fecha));
  return { ok: true, fechaBase: d.fechaBase ?? null, desde: d.desde ?? null, hasta: d.hasta ?? null, porMedio };
}

/** El dia que mas bajo el saldo (la diferencia del dia mas negativa). null si ningun dia tiene faltante medido. */
export function peorDia(dias) {
  let peor = null;
  for (const d of dias || []) if (d.diferenciaDia !== null && d.diferenciaDia < 0 && (!peor || d.diferenciaDia < peor.diferenciaDia)) peor = d;
  return peor;
}

/** Lo que se conto o lo que dice el banco, en palabras de la columna. */
export function medidoDelDia(d) {
  if (d.medio === 'efectivo') return d.contado === null ? { texto: 'sin arqueo', valor: null } : { texto: `${d.arqueos ?? 1} arqueo${(d.arqueos ?? 1) === 1 ? '' : 's'}`, valor: d.contado };
  if (d.conExtracto === false || d.bancoNeto === null) return { texto: 'sin extracto', valor: null };
  return { texto: 'extracto', valor: d.bancoNeto };
}

/** Faltante = diferencia del dia negativa (rojo); sobrante positivo; null = sin medida (gris, nunca "0"). */
export function tonoDelDia(d) {
  if (d.diferenciaDia === null) return 'sin-medida';
  if (d.diferenciaDia < 0) return 'faltante';
  if (d.diferenciaDia > 0) return 'sobrante';
  return 'cuadra';
}
